/**
 * Runtime configuration loaded after build from config.json or config.js.
 *
 * Deploy layout (e.g. serve from /crash/):
 *   crash/config.json  — fetched on startup (editable without rebuild)
 *   crash/config.js    — optional; sets window.__RUNTIME_CONFIG__ before the app bundle
 *
 * Empty or missing values fall back to VITE_* build env, then hardcoded defaults
 * (https://be.play-studios.ai / wss://be.play-studios.ai/ws/stomp).
 */

export interface RuntimeConfig {
  apiUrl?: string;
  wsUrl?: string;
  lobbyUrl?: string;
  /** snake_case aliases for ops-friendly JSON */
  api_url?: string;
  ws_url?: string;
  lobby_url?: string;
}

let loaded = false;
let overrides: Pick<RuntimeConfig, 'apiUrl' | 'wsUrl' | 'lobbyUrl'> = {};

function pickUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeConfig(raw: RuntimeConfig | undefined) {
  if (!raw) return;
  const apiUrl = pickUrl(raw.apiUrl ?? raw.api_url);
  const wsUrl = pickUrl(raw.wsUrl ?? raw.ws_url);
  const lobbyUrl = pickUrl(raw.lobbyUrl ?? raw.lobby_url);
  overrides = {
    ...(apiUrl ? { apiUrl } : {}),
    ...(wsUrl ? { wsUrl } : {}),
    ...(lobbyUrl ? { lobbyUrl } : {}),
  };
}

/**
 * Loads runtime overrides once. Safe to call multiple times.
 * Order: window.__RUNTIME_CONFIG__ (config.js) → fetch config.json → build defaults.
 */
export async function loadRuntimeConfig(): Promise<void> {
  if (loaded) return;

  // Deploy config.json is for production; local dev uses the Vite proxy instead.
  if (import.meta.env.DEV) {
    loaded = true;
    console.debug(
      '[runtime-config] Skipping runtime overrides in development (Vite proxy).'
    );
    return;
  }

  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    normalizeConfig(window.__RUNTIME_CONFIG__);
    loaded = true;
    if (import.meta.env.DEV) {
      console.debug('[runtime-config] Applied window.__RUNTIME_CONFIG__', overrides);
    }
    return;
  }

  if (typeof window !== 'undefined') {
    try {
      const base = (import.meta.env.BASE_URL ?? '/').replace(/\/?$/, '/');
      const response = await fetch(`${base}config.json`, { cache: 'no-store' });
      if (response.ok) {
        normalizeConfig((await response.json()) as RuntimeConfig);
        if (import.meta.env.DEV || Object.keys(overrides).length > 0) {
          console.debug('[runtime-config] Applied config.json', overrides);
        }
      }
    } catch (error) {
      console.warn(
        '[runtime-config] Could not load config.json; using build defaults.',
        error
      );
    }
  }

  loaded = true;
}

const API_URL_SESSION_KEY = 'playgrid-api-url';
const LOBBY_URL_SESSION_KEY = 'playgrid-lobby-url';

function getUrlFromGameQuery(
  sessionKey: string,
  paramKeys: readonly [string, string],
  options: { allowSessionFallback?: boolean } = {}
): string | undefined {
  const allowSessionFallback = options.allowSessionFallback !== false;
  if (typeof window === 'undefined') return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = pickUrl(params.get(paramKeys[0]) ?? params.get(paramKeys[1]));
    if (fromQuery) {
      try {
        window.sessionStorage.setItem(sessionKey, fromQuery);
      } catch {
        /* ignore */
      }
      return fromQuery;
    }
    if (!allowSessionFallback) return undefined;
    try {
      return pickUrl(window.sessionStorage.getItem(sessionKey));
    } catch {
      return undefined;
    }
  } catch {
    return undefined;
  }
}

/**
 * Derive STOMP URL from a single backend HTTP(S)/WS endpoint.
 * `https://host` → `wss://host/ws/stomp`; already-`/ws/stomp` WS URLs pass through.
 */
export function deriveWsUrlFromEndpoint(endpoint: string): string | undefined {
  try {
    const url = new URL(endpoint.trim());
    const path = url.pathname.replace(/\/$/, '') || '';
    if (url.protocol === 'ws:' || url.protocol === 'wss:') {
      return path.endsWith('/ws/stomp')
        ? `${url.protocol}//${url.host}/ws/stomp`
        : `${url.protocol}//${url.host}${path || '/ws/stomp'}`;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return undefined;
    }
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}/ws/stomp`;
  } catch {
    return undefined;
  }
}

/** Single backend endpoint from the game launch query (`apiUrl` / `api_url`). */
export function getApiUrlFromGameQuery(): string | undefined {
  return getUrlFromGameQuery(API_URL_SESSION_KEY, ['apiUrl', 'api_url']);
}

export function peekLobbyUrlFromSearch(): string | undefined {
  return getUrlFromGameQuery(LOBBY_URL_SESSION_KEY, ['lobbyUrl', 'lobby_url'], {
    allowSessionFallback: false,
  });
}

/** True when launch URL has `isPartner=true` (or `1`). */
export function isPartnerFromSearch(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const value = new URLSearchParams(window.location.search).get('isPartner');
    return value === 'true' || value === '1';
  } catch {
    return false;
  }
}

export function getLobbyUrlFromGameQuery(): string | undefined {
  return getUrlFromGameQuery(LOBBY_URL_SESSION_KEY, ['lobbyUrl', 'lobby_url'], {
    allowSessionFallback: true,
  });
}

/**
 * API base URL: launch `apiUrl` / `api_url` wins, then config.json.
 */
export function getRuntimeApiUrl(): string | undefined {
  return getApiUrlFromGameQuery() ?? overrides.apiUrl;
}

/**
 * STOMP WebSocket URL: derived from launch `apiUrl` when present, else config.json `wsUrl`.
 */
export function getRuntimeWsUrl(): string | undefined {
  const launchEndpoint = getApiUrlFromGameQuery();
  if (launchEndpoint) {
    return deriveWsUrlFromEndpoint(launchEndpoint) ?? overrides.wsUrl;
  }
  return overrides.wsUrl;
}

/**
 * Lobby override for redirects: game URL `lobbyUrl` wins, then config.json.
 */
export function getRuntimeLobbyUrl(): string | undefined {
  return peekLobbyUrlFromSearch() ?? overrides.lobbyUrl;
}
